/*
 * Copyright 2025 JieYang(aletme).
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 */

// Main entry file
import { GlobalSvgLoader } from "./loader";
import type { SvgLoaderOptions, SvgModule } from "./types";

// Export types
export * from "./types";

// Export singleton instance
export const svgLoader = GlobalSvgLoader.getInstance();

// Export convenience methods
// Get SVG
export const getSvg = (iconName: string, color?: string): Promise<string> =>
  svgLoader.getSvg(iconName, color);
// Get SVG from cache
export const getSvgCache = (iconName: string, color?: string): string =>
  svgLoader.getSvgCache(iconName, color);
export const hasSvg = (iconName: string): boolean => svgLoader.hasSvg(iconName);
// Check if FontAwesome icon
export const isFontAwesomeIcon = (iconName: string): boolean =>
  svgLoader.isFontAwesomeIcon(iconName);
export const initialize = (
  options?: SvgLoaderOptions
): Promise<void> => svgLoader.initialize(options);
// Get loaded SVG names
export const getLoadedSvgNames = (): string[] => svgLoader.getLoadedSvgNames();
// Clear cache
export const clearSvgCache = (): void => svgLoader.clearCache();
// Reinitialize loader
export const reinitialize = (
  options?: SvgLoaderOptions
): Promise<void> => svgLoader.reinitialize(options);
// Set SVG modules
export const setSvgModules = (svgModules: SvgModule): void =>
  svgLoader.setSvgModules(svgModules);
// Clear SVG modules
export const clearSvgModules = (): void => svgLoader.clearSvgModules();
// Set debug mode
export const setDebugMode = (enabled: boolean): void =>
  svgLoader.setDebugMode(enabled);
// Get debug mode status
export const getDebugMode = (): boolean => svgLoader.getDebugMode();

// Compatibility export
export class SvgLoader {
  // Load SVG
  static async loadSvg(iconName: string, color?: string): Promise<string> {
    return svgLoader.getSvg(iconName, color);
  }

  // Load SVG from cache
  static loadSvgCache(iconName: string, color?: string): string {
    return svgLoader.getSvgCache(iconName, color);
  }

  // Check if SVG exists
  static hasSvg(iconName: string): boolean {
    return svgLoader.hasSvg(iconName);
  }

  // Check if FontAwesome icon
  static isFontAwesomeIcon(iconName: string): boolean {
    return svgLoader.isFontAwesomeIcon(iconName);
  }

  // Initialize loader
  static async initialize(
    options: SvgLoaderOptions = {
      preloadAll: false,
      debugMode: false,
      svgModules: undefined,
    }
  ): Promise<void> {
    return svgLoader.initialize(options);
  }

  // Reinitialize loader
  static async reinitialize(
    options: SvgLoaderOptions = {
      preloadAll: false,
      debugMode: false,
      svgModules: undefined,
    }
  ): Promise<void> {
    return svgLoader.reinitialize(options);
  }

  // Cache management
  static getLoadedSvgNames(): string[] {
    return svgLoader.getLoadedSvgNames();
  }

  // Clear cache
  static clearCache(): void {
    svgLoader.clearCache();
  }

  // SVG module management
  static setSvgModules(svgModules: SvgModule): void {
    svgLoader.setSvgModules(svgModules);
  }

  // Clear SVG modules
  static clearSvgModules(): void {
    svgLoader.clearSvgModules();
  }

  // Debug functionality
  static setDebugMode(enabled: boolean): void {
    svgLoader.setDebugMode(enabled);
  }

  // Get debug mode status
  static getDebugMode(): boolean {
    return svgLoader.getDebugMode();
  }

  // Deprecated methods
  static async preloadSvgs(_iconNames: string[]): Promise<void> {
    // In the new version, all SVGs are preloaded during initialization
    console.warn(
      "preloadSvgs is deprecated. All SVGs are preloaded during initialization."
    );
  }
}

// Default export
export { svgLoader as default };
